import { gql, useQuery } from '@apollo/client';
import { GraphQLSchema } from 'graphql';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { PreflightScriptEditModal } from 'src/app/graph/explorerPage/explorerSettings/PreflightScriptEditModal';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from 'src/app/graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { ScriptEditorModal } from 'src/app/graph/explorerPage/scriptPane/ScriptEditorModal';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { KeyboardShortcutModal } from 'src/components/common/keyboardShortcutModal/KeyboardShortcutModal';
import { Loading } from 'src/components/common/loading/Loading';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { URLModal } from 'src/components/common/URLModal';
import { PermissionBlock } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import {
  readFromLocalStorage,
  useLocalStorage,
} from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { useTrackMutation } from 'src/hooks/useTrackMutation'; // eslint-disable-line no-restricted-imports
import { EventCategory } from 'src/lib/analytics';
import { getAmplitudeApplicationMode } from 'src/lib/analytics/amplitude/helpers/getAmplitudeApplicationMode';
import {
  trackIntrospectionRequestError,
  trackIntrospectionRequestSuccess,
} from 'src/lib/analytics/amplitude/helpers/trackIntrospectionRequestStatus';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';
import {
  buildSubgraphMetaHelpers,
  subgraphMetaSubgraphsFragment,
} from 'src/lib/graphql/subgraphMetadataHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { EventEnum } from 'src/lib/graphqlTypes/types';
import { isLocalUrl } from 'src/lib/isLocalUrl';
import { useLDFlag } from 'src/lib/launchDarkly';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';
import { mergeQueryParams, setOverlayVisibleLocation } from 'src/lib/routing';

import {
  explorerKeyboardShortcuts,
  hotkeys,
  useKeypressListener,
} from '../../../hooks/useKeypressListener/useKeypressListener';
import { usePerKeyLocalStorage } from '../../../hooks/usePerKeyLocalStorage';
import { useEndpointReachability } from '../hooks/useEndpointReachability';
import { GraphRef, useGraphRef } from '../hooks/useGraphRef';
import { useIsLoggedIn } from '../hooks/useIsLoggedIn';
import { useSchema } from '../hooks/useSchema';
import { sandboxGraphRouteConfig } from '../routes';
import { SANDBOX_EMPTY_SCHEMA } from '../sandboxShared/defaultSandboxSchema';
import { NoSchemaFoundCard } from '../shared/NoSchemaFoundCard';
import { GraphBehavioralFlags } from '../variantLayout/graphBehavioralFlags/graphBehavioralFlags';

import { LinkEndpointModalData } from './connectionSettingsModal/ConnectionSettingsModal';
import { EmbedExplorerModal } from './embedExplorerModal/EmbedExplorerModal';
import { EnvironmentVariablesEditor } from './environmentVariablesEditor/EnvironmentVariablesEditor';
import { ExplorerBehavioralFlags } from './explorerBehavioralFlags';
import { ExplorerDocs } from './explorerDocs/ExplorerDocs';
import { ExplorerHeadersEditor } from './explorerHeadersEditor/ExplorerHeadersEditor';
import {
  ExplorerPaneLayout,
  useSharedExplorerLayoutInfo,
} from './explorerPaneLayout/ExplorerPaneLayout';
import { ExplorerSettings } from './explorerSettings/ExplorerSettings';
import { PreflightScriptOAuthConfirmationModal } from './explorerSettings/PreflightScriptOAuthConfirmationModal';
import { ScriptPromptModal } from './explorerSettings/ScriptPromptModal';
import {
  ExplorerSettingsType,
  useExplorerSettings,
} from './explorerSettings/useExplorerSettings';
import { ExplorerTabs } from './explorerTabs/ExplorerTabs';
import { getDocumentDefinitions } from './helpers/graphqlParseHelpers';
import { AnalyticsProvider } from './hooks/useAnalyticsContext';
import { useDerivedEditorValue } from './hooks/useDerivedEditorValue';
import { getDefaultTabState } from './hooks/useExplorerState/getDefaultTabState';
import { CollectionEntryState } from './hooks/useExplorerState/useEditorTabState/collectionEntryState';
import { EditorTabStateProviders } from './hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { useExplorerState } from './hooks/useExplorerState/useExplorerState';
import { IntrospectionFailure } from './hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { useLoadExplorerStateFromURL } from './hooks/useLoadExplorerStateFromURL';
import { OperationCollections } from './operationCollections/OperationCollections';
import { OperationCollectionsModals } from './operationCollections/operationCollectionsModals/OperationCollectionsModals';
import { OperationHistory } from './operationHistory/OperationHistory';
import { OperationsPane } from './operationsPane/OperationsPane';
import { PublicUrlConfirmationModal } from './PublicUrlConfirmationModal';
import { ResultsPane } from './resultsPane/ResultsPane';
import { ScriptPane } from './scriptPane/ScriptPane';
import { TriggerableSearch } from './search/triggerableSearch/TriggerableSearch';
import { ShareableLinkSettingsModal } from './shareableLinkSettingsModal/ShareableLinkSettingsModal';
import { VariablesEditor } from './variablesEditor/VariablesEditor';

export interface ConnectionSettings {
  url: string | null;
  subscriptionUrl: string | null;
  sendCookies: boolean | null;
}

interface ExplorerPageBodyProps {
  graphRef: GraphRef | null;
  connectionSettings: ConnectionSettings;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  schemaInfo: {
    schema: GraphQLSchema;
    isSupergraph: boolean;
    createdAt?: string | number;
    hash?: string;
    hasSupergraphSchema: boolean;
    subgraphMetaHelpers: ReturnType<typeof buildSubgraphMetaHelpers>;
  };
  explorerSettings: ExplorerSettingsType;
  introspectionFailure: IntrospectionFailure | undefined;
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
  isPublic: boolean;
  isCloudSupergraph: boolean;
  accountId?: string;
  graphBehavioralFlags: GraphBehavioralFlags;
}

const ExplorerPageBody = ({
  graphRef,
  connectionSettings,
  explorerBehavioralFlags,
  schemaInfo,
  explorerSettings,
  introspectionFailure,
  openEditUrlModal,
  isCloudSupergraph,
  accountId,
  graphBehavioralFlags,
}: ExplorerPageBodyProps) => {
  const explorerLayoutInfo = useSharedExplorerLayoutInfo(
    explorerBehavioralFlags,
  );

  /**
   * URL GraphQL queries will be made to
   */
  const endpointUrl = connectionSettings.url;

  const includeCookies = connectionSettings.sendCookies ?? false;

  const {
    submitOperation,
    operationHistory,
    subscription,
    loadOperation,
    editor,
    variablesEditor,
    preflightOperationScriptEditor,
    postflightOperationScriptEditor,
    mockedResponseEditor,
    environmentVariablesEditor,
    editorTabState,
    resultsTab,
    setResultsTab,
    dispatchDocsNavAction,
    searchState,
    dispatchSearchAction,
    submitForResponseHints,
    hasPublicUrlBeenConfirmed,
    publicUrlConfirmationModalState,
    promptRequest,
    oauth2Request,
  } = useExplorerState({
    endpointUrl,
    subscriptionUrl: connectionSettings.subscriptionUrl,
    includeCookies,
    schema: schemaInfo.schema,
    explorerBehavioralFlags,
    explorerSettings,
    setDocsTab: explorerLayoutInfo.setDocsTab,
    openEditUrlModal,
    graphRef,
  });
  const { result: operationResult, docsPath } = editorTabState;

  const endpointReachability = useEndpointReachability();

  useLoadExplorerStateFromURL({
    dispatchSearchAction,
    schema: schemaInfo.schema,
    editorTabState,
    sandboxConnectionSettingsInitialValue:
      graphBehavioralFlags.sandboxConnectionSettingsInitialValue,
  });

  const getDerivedValue = useCallback(
    (editorValue: string) =>
      getDocumentDefinitions(
        editorValue,
        getRootTypeNames(schemaInfo.schema),
      ).fragmentDefinitions.map((definition) => definition.type),
    [schemaInfo.schema],
  );
  const fragmentTypeNames = useDerivedEditorValue({
    editor,
    getValue: getDerivedValue,
    withPosition: false,
  });

  const localStorageGraphIdentifierForTracking = useLocalStorageGraphIdentifier(
    {
      shouldNamespaceSandboxByEndpoint: false,
      shouldOnlyStoreForRegisteredGraphs: false,
    },
  );
  const trackMutation = useTrackMutation();
  const trackCustomEvent = useTrackCustomEvent();
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;
  const { isLoggedIn } = useIsLoggedIn();
  const [sandboxUrl] = useLocalStorage('sandboxUrl');
  const [sandboxConnectionSettings] = usePerKeyLocalStorage({
    localStorageKey: 'sandboxConnectionSettings',
    key: sandboxUrl,
    initialValue:
      graphBehavioralFlags.sandboxConnectionSettingsInitialValue(sandboxUrl),
  });

  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [protocol] = usePerGraphIdentifierLocalStorage({
    key: 'graphqlSubscriptionProtocol',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: 'auto-detect',
  });

  const urlForSubscriptions =
    protocol === 'http-multipart'
      ? connectionSettings.url
      : connectionSettings.subscriptionUrl;

  const submitOperationOrEditEndpoint = React.useCallback(
    ({
      isSubscription,
      ...args
    }: {
      operationName: string | undefined;
      body: string;
      isSubscription: boolean;
    }) => {
      ampli.explorerOperationSubmitted({
        'Application Mode': getAmplitudeApplicationMode(),
        'Graph Operation': EventEnum.RUN_EXPLORER_OPERATION,
        'Graph ID': localStorageGraphIdentifierForTracking?.domain ?? 'unknown',
        'Graph Variant':
          localStorageGraphIdentifierForTracking?.id ?? 'unknown',
      });

      trackMutation({
        event: EventEnum.RUN_EXPLORER_OPERATION,
        // TODO: rename graphId / graphVariant in this track
        // since these could be from sandbox or embedded
        graphID: localStorageGraphIdentifierForTracking?.domain ?? 'unknown',
        graphVariant: localStorageGraphIdentifierForTracking?.id ?? 'unknown',
      });

      // fire ga4 event
      trackCustomEvent({
        action: 'user_action',
        // Old event, this style of category is deprecated
        category: 'Ran a query' as EventCategory,
        label: 'User actions',
        secondaryLabel: `preflightScriptEnabled:${
          explorerSettings.isPreflightScriptEnabled
        },hasPreflightScript:${!!explorerSettings.preflightScript}`,
      });

      if (isSandboxRoute) {
        const sandboxEndpointOrEmpty = sandboxUrl ?? '';
        const trackingCategory = isLocalUrl(sandboxEndpointOrEmpty)
          ? 'Sandbox - Local'
          : 'Sandbox - Remote';
        // fire ga4 event
        trackCustomEvent({
          action: 'run_explorer_operation',
          category: trackingCategory,
          label: sandboxEndpointOrEmpty,
          isAutoupdatingSchema:
            sandboxConnectionSettings.shouldAutoUpdateSchema,
        });
      }

      if (isSubscription) {
        if (
          (explorerBehavioralFlags.shouldPostMessageOperations &&
            !explorerBehavioralFlags.requiresConfiguredUrls) ||
          urlForSubscriptions
        ) {
          subscription.submitSubscription(args);
        } else {
          openEditUrlModal('Run query');
        }
      } else {
        submitOperation(args);
      }
    },
    [
      trackMutation,
      localStorageGraphIdentifierForTracking?.domain,
      localStorageGraphIdentifierForTracking?.id,
      trackCustomEvent,
      explorerSettings.isPreflightScriptEnabled,
      explorerSettings.preflightScript,
      isSandboxRoute,
      sandboxUrl,
      sandboxConnectionSettings.shouldAutoUpdateSchema,
      explorerBehavioralFlags.shouldPostMessageOperations,
      explorerBehavioralFlags.requiresConfiguredUrls,
      urlForSubscriptions,
      subscription,
      openEditUrlModal,
      submitOperation,
    ],
  );

  const shouldManageVariables = explorerSettings.explorerManageVariablesSetting;

  const canBeEmbedded = !explorerBehavioralFlags.cannotBeEmbedded;

  const [defaultTabState, setDefaultTabState] =
    useState<CollectionEntryState | null>(null);
  useEffect(() => {
    let cancelled = false;
    setDefaultTabState(null);
    getDefaultTabState(schemaInfo.schema).then(
      (s) => !cancelled && setDefaultTabState(s),
    );
    return () => {
      cancelled = true;
    };
  }, [schemaInfo.schema]);

  // Keyboard modal shortcut setup
  const [keyboardModal, setKeyboardModal] = useState<boolean>(false);
  useKeypressListener(hotkeys.toggleShortcutModal, (e) => {
    setKeyboardModal(!keyboardModal);
    e?.preventDefault();
    e?.stopPropagation();
  });

  const dragAndDropRef = React.useRef<HTMLDivElement>(null);

  const openSearch = React.useCallback(
    () => dispatchSearchAction({ type: 'open' }),
    [dispatchSearchAction],
  );

  const openEditUrlModalToSet = React.useCallback(
    () => openEditUrlModal('Set url'),
    [openEditUrlModal],
  );

  const shouldShowPostflightScripts = useLDFlag('nebula-postflight-scripts');

  return (
    <ErrorBoundary
      extraErrorData={() => ({
        studioDocument: editor.getValue(),
      })}
    >
      <TriggerableSearch
        dispatchDocsNavAction={dispatchDocsNavAction}
        schema={schemaInfo.schema}
        fragmentTypeNames={fragmentTypeNames}
        searchState={searchState}
        dispatchSearchAction={dispatchSearchAction}
        getSubgraphs={schemaInfo.subgraphMetaHelpers?.getSubgraphs}
      />
      <URLModal name={Config.modals.explorerShareableLinkSettings}>
        {({ isOpen, closeModal }) => (
          <ShareableLinkSettingsModal isOpen={isOpen} onClose={closeModal} />
        )}
      </URLModal>
      {graphRef && (
        <URLModal name={Config.modals.embedExplorer}>
          {({ isOpen, closeModal }) => (
            <EmbedExplorerModal
              isOpen={isOpen}
              graphRef={graphRef}
              onClose={closeModal}
              accountId={accountId}
              defaultTabState={defaultTabState}
            />
          )}
        </URLModal>
      )}
      {(graphRef || (isSandboxRoute && isLoggedIn)) && (
        <URLModal name={Config.modals.preflightScript}>
          {({ closeModal }) => (
            <PreflightScriptEditModal
              endpointUrl={endpointUrl}
              onClose={closeModal}
              currentPreflightScript={explorerSettings.preflightScript}
              graphRef={graphRef}
              includeCookies={includeCookies}
              explorerBehavioralFlags={explorerBehavioralFlags}
              isOperationScriptEnabled={
                explorerSettings.isOperationScriptEnabled
              }
              setDocsTab={explorerLayoutInfo.setDocsTab}
              graphqlSchema={schemaInfo.schema}
            />
          )}
        </URLModal>
      )}
      {(graphRef || (isSandboxRoute && isLoggedIn)) && (
        <URLModal name={Config.modals.preflightOperationScript}>
          {({ closeModal }) => (
            <ScriptEditorModal
              endpointUrl={endpointUrl}
              onClose={closeModal}
              scriptEditor={preflightOperationScriptEditor}
              graphRef={graphRef}
              editorTabState={editorTabState}
              includeCookies={includeCookies}
              explorerBehavioralFlags={explorerBehavioralFlags}
              isOperationScriptEnabled={
                explorerSettings.isOperationScriptEnabled
              }
              setDocsTab={explorerLayoutInfo.setDocsTab}
              graphqlSchema={schemaInfo.schema}
              scriptType="preflightOperation"
              mockedResponseEditor={mockedResponseEditor}
            />
          )}
        </URLModal>
      )}
      {shouldShowPostflightScripts &&
        (graphRef || (isSandboxRoute && isLoggedIn)) && (
          <URLModal name={Config.modals.postflightOperationScript}>
            {({ closeModal }) => (
              <ScriptEditorModal
                endpointUrl={endpointUrl}
                onClose={closeModal}
                scriptEditor={postflightOperationScriptEditor}
                graphRef={graphRef}
                editorTabState={editorTabState}
                includeCookies={includeCookies}
                explorerBehavioralFlags={explorerBehavioralFlags}
                isOperationScriptEnabled={
                  explorerSettings.isOperationScriptEnabled
                }
                setDocsTab={explorerLayoutInfo.setDocsTab}
                graphqlSchema={schemaInfo.schema}
                scriptType="postflightOperation"
                mockedResponseEditor={mockedResponseEditor}
              />
            )}
          </URLModal>
        )}
      <div ref={dragAndDropRef} className="relative z-50" />
      {oauth2Request && (
        <PreflightScriptOAuthConfirmationModal
          {...oauth2Request}
          isOpen={!!oauth2Request}
        />
      )}
      {promptRequest && <ScriptPromptModal {...promptRequest} />}
      {
        // public graph urls will always have a graphRef
      }
      {publicUrlConfirmationModalState && graphRef && (
        <PublicUrlConfirmationModal
          isOpen
          preflightScript={publicUrlConfirmationModalState.preflightScript}
          resolvePublicUrlConfirmed={
            publicUrlConfirmationModalState.resolvePublicUrlConfirmed
          }
          url={publicUrlConfirmationModalState.url}
          isSubscription={publicUrlConfirmationModalState.isSubscription}
          canApproveSingleRun={
            publicUrlConfirmationModalState.canApproveSingleRun
          }
        />
      )}
      {keyboardModal && (
        <KeyboardShortcutModal
          onClose={() => setKeyboardModal(false)}
          shortcuts={explorerKeyboardShortcuts}
        />
      )}
      <OperationCollectionsModals
        dispatchSavedOperationLocalStateAction={
          editorTabState.dispatchSavedOperationLocalStateAction
        }
      />
      <ExplorerPaneLayout
        shouldShowPostflightScripts={shouldShowPostflightScripts}
        explorerBehavioralFlags={explorerBehavioralFlags}
        layoutInfo={explorerLayoutInfo}
        dispatchSearchAction={dispatchSearchAction}
        explorerDocs={
          <ExplorerDocs
            graphRef={graphRef}
            editor={editor}
            variablesEditor={variablesEditor}
            docsPath={docsPath}
            dispatchDocsNavAction={dispatchDocsNavAction}
            schema={schemaInfo.schema}
            createdAt={schemaInfo.createdAt || 'n/a'}
            hash={schemaInfo.hash || 'n/a'}
            setActiveEditorBottomTab={
              explorerLayoutInfo.setActiveEditorBottomTab
            }
            shouldManageVariables={shouldManageVariables}
            explorerBehavioralFlags={explorerBehavioralFlags}
            openSearch={openSearch}
            isSupergraph={schemaInfo.isSupergraph}
            getIsEntity={schemaInfo.subgraphMetaHelpers?.getIsEntity}
          />
        }
        operationCollections={
          <OperationCollections
            graphRef={graphRef}
            editorTabState={editorTabState}
            setDocsTab={explorerLayoutInfo.setDocsTab}
            dragAndDropRef={dragAndDropRef}
          />
        }
        explorerHistory={
          <OperationHistory
            operationHistory={operationHistory}
            loadOperation={loadOperation}
          />
        }
        explorerSettings={
          <ExplorerSettings
            graphRef={graphRef}
            settings={explorerSettings}
            settingsEndpointUrl={endpointUrl}
            settingsSubscriptionUrl={connectionSettings.subscriptionUrl}
            openEditUrlModal={openEditUrlModalToSet}
            explorerBehavioralFlags={explorerBehavioralFlags}
            hasPublicUrlBeenConfirmed={hasPublicUrlBeenConfirmed}
            canBeEmbedded={canBeEmbedded}
            environmentVariablesEditor={environmentVariablesEditor}
          />
        }
        operationsPane={
          <OperationsPane
            isCloudSupergraph={isCloudSupergraph}
            graphRef={graphRef}
            graphURL={endpointUrl}
            submitOperation={submitOperationOrEditEndpoint}
            editor={editor}
            variablesEditor={variablesEditor}
            preflightOperationScriptEditor={preflightOperationScriptEditor}
            postflightOperationScriptEditor={postflightOperationScriptEditor}
            editorTabState={editorTabState}
            includeCookies={includeCookies}
            schema={schemaInfo.schema}
            hasSupergraphSchema={schemaInfo.hasSupergraphSchema}
            setActiveEditorBottomTab={
              explorerLayoutInfo.setActiveEditorBottomTab
            }
            submitForResponseHints={submitForResponseHints}
            responseHints={explorerSettings.responseHints}
            fieldTimingsPercentile={explorerSettings.fieldTimingsPercentile}
            setKeyboardModal={setKeyboardModal}
            shouldManageVariables={shouldManageVariables}
            operationResult={operationResult}
            explorerBehavioralFlags={explorerBehavioralFlags}
            canBeEmbedded={canBeEmbedded}
          />
        }
        variablesPane={
          <VariablesEditor
            className="size-full"
            schema={schemaInfo.schema}
            operationEditor={editor}
            variablesEditor={variablesEditor}
            settingsEndpointUrl={endpointUrl}
            settingsSubscriptionUrl={connectionSettings.subscriptionUrl}
            explorerBehavioralFlags={explorerBehavioralFlags}
            editorTabState={editorTabState}
          />
        }
        environmentVariablesPane={
          <EnvironmentVariablesEditor
            className="size-full"
            editor={environmentVariablesEditor}
          />
        }
        headersPane={
          <ExplorerHeadersEditor
            graphRef={graphRef}
            explorerBehavioralFlags={explorerBehavioralFlags}
            graphBehavioralFlags={graphBehavioralFlags}
            settingsEndpointUrl={endpointUrl}
            settingsSubscriptionUrl={connectionSettings.subscriptionUrl}
          />
        }
        preflightOperationScriptPane={
          <ScriptPane
            scriptEditor={preflightOperationScriptEditor}
            isOperationScriptEnabled={explorerSettings.isOperationScriptEnabled}
            setDocsTab={explorerLayoutInfo.setDocsTab}
            scriptType="preflightOperation"
          />
        }
        postflightOperationScriptPane={
          <ScriptPane
            scriptEditor={postflightOperationScriptEditor}
            isOperationScriptEnabled={explorerSettings.isOperationScriptEnabled}
            setDocsTab={explorerLayoutInfo.setDocsTab}
            scriptType="postflightOperation"
          />
        }
        resultsPane={
          <ResultsPane
            operationResult={operationResult}
            resultsTab={resultsTab}
            setResultsTab={setResultsTab}
            endpointReachability={endpointReachability}
            introspectionFailure={introspectionFailure}
            openEditUrlModal={openEditUrlModal}
            subscription={subscription}
            setIsTableMode={explorerSettings.setIsTableMode}
            isTableMode={explorerSettings.isTableMode}
            setShowMermaidQueryPlan={explorerSettings.setShowMermaidQueryPlan}
            showMermaidQueryPlan={explorerSettings.showMermaidQueryPlan}
            isMockingResponses={explorerSettings.isMockingResponses}
            endpointUrl={connectionSettings.url}
            subscriptionUrl={urlForSubscriptions}
            explorerBehavioralFlags={explorerBehavioralFlags}
            showTraces={explorerSettings.showTraces}
            editor={editor}
            schema={schemaInfo.schema}
            hasSupergraphSchema={schemaInfo.hasSupergraphSchema}
            accountId={accountId}
            graphRef={graphRef}
            subgraphMap={schemaInfo.subgraphMetaHelpers?.subgraphMap}
          />
        }
        explorerTabs={<ExplorerTabs editorTabState={editorTabState} />}
      />
    </ErrorBoundary>
  );
};

/**
 * Wrapper around ExplorerPageBody to control graph redirects.
 */
export const ExplorerPage = ({
  userId,
  explorerBehavioralFlags,
  graphBehavioralFlags,
}: {
  userId?: string;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  graphBehavioralFlags: GraphBehavioralFlags;
}) => {
  const graphRef = useGraphRef();
  useTrackL1PageView('explorer', { graphRef });

  const {
    endpoint: endpointForSandbox,
    subscriptionEndpoint: subscriptionEndpointForSandbox,
    // eslint-disable-next-line local/no-direct-route-config-access
  } = useRouteParams(sandboxGraphRouteConfig, catchAllRouteConfig);
  const location = useLocation<{
    linkEndpointModalData: LinkEndpointModalData;
  }>();
  const history = useHistory<{
    linkEndpointModalData: LinkEndpointModalData;
  }>();

  const {
    data: studioPageQueryData,
    loading: loadingIdentity,
    error: errorLoadingIdentity,
  } = useQuery<
    GraphQLTypes.ExplorerPageQuery,
    GraphQLTypes.ExplorerPageQueryVariables
  >(
    gql`
      query ExplorerPageQuery($serviceId: ID!, $graphVariant: String!) {
        service(id: $serviceId) {
          id
          account {
            id
          }
          variant(name: $graphVariant) {
            id
            hasSupergraphSchema
            isContract
            isPublic
            name
            url
            sendCookies
            subscriptionUrl
            activeSchemaPublish {
              id
              compositionResult {
                graphCompositionID
                ...SubgraphMetaSubgraphsFragment
              }
            }
            permissions {
              canQuerySchemas
            }
            graph {
              id
              graphType
            }

            entities {
              ... on EntitiesResponse {
                entities {
                  subgraphKeys {
                    keys
                    subgraphName
                  }
                  typename
                }
              }
            }
          }
        }
      }

      ${subgraphMetaSubgraphsFragment}
    `,
    {
      variables: {
        serviceId: graphRef?.graphId as string,
        graphVariant: graphRef?.graphVariant as string,
      },
      skip: !graphRef,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const explorerSettings = useExplorerSettings(explorerBehavioralFlags);

  const [sandboxUrl, setSandboxUrl] = useLocalStorage('sandboxUrl');

  const [sandboxConnectionSettings, _, setSandboxConnectionSettingsForKey] =
    usePerKeyLocalStorage({
      localStorageKey: 'sandboxConnectionSettings',
      key: sandboxUrl,
      initialValue:
        graphBehavioralFlags.sandboxConnectionSettingsInitialValue(sandboxUrl),
    });

  const sandboxConnectionSettingsWithUrl = useMemo(() => {
    return {
      ...sandboxConnectionSettings,
      url: sandboxUrl,
    };
  }, [sandboxConnectionSettings, sandboxUrl]);

  React.useEffect(() => {
    const setFreshSandboxConnectionSettings = (
      currentUrl: string,
      newSubscriptionUrl: string | undefined,
    ) => {
      if (
        newSubscriptionUrl &&
        !Object.keys(
          readFromLocalStorage('sandboxConnectionSettings'),
        ).includes(currentUrl)
      ) {
        setSandboxConnectionSettingsForKey(
          {
            ...graphBehavioralFlags.sandboxConnectionSettingsInitialValue(
              currentUrl,
            ),
            subscriptionUrl: newSubscriptionUrl,
          },
          currentUrl,
        );
      }
    };
    if (endpointForSandbox) {
      setSandboxUrl(endpointForSandbox);
      setFreshSandboxConnectionSettings(
        endpointForSandbox,
        subscriptionEndpointForSandbox,
      );
      history.replace({
        ...history.location,
        search: mergeQueryParams(history.location.search, {
          [Config.queryParameters.ExplorerEndpoint]: undefined,
          [Config.queryParameters.ExplorerSubscriptionEndpoint]: undefined,
        }),
      });
    }
  }, [
    endpointForSandbox,
    setSandboxConnectionSettingsForKey,
    setSandboxUrl,
    history,
    graphBehavioralFlags,
    subscriptionEndpointForSandbox,
  ]);

  const variantData = studioPageQueryData?.service?.variant;
  const connectionSettings = graphRef
    ? variantData
    : sandboxConnectionSettingsWithUrl;

  const {
    activeSchema,
    createdAt,
    hash,
    error: schemaError,
    initialLoadingSchema,
  } = useSchema();

  useEffect(() => {
    if (activeSchema) {
      trackIntrospectionRequestSuccess({
        graphRef: graphRef || undefined,
        graphUri: connectionSettings?.url ?? undefined,
      });
    }
  }, [activeSchema, connectionSettings, graphRef]);

  useEffect(() => {
    if (schemaError) {
      trackIntrospectionRequestError({
        error: { message: 'Introspection failed' },
      });
    }
  }, [schemaError]);

  const introspectionFailure =
    typeof schemaError === 'string' ? schemaError : undefined;

  const subgraphConfigs =
    variantData?.activeSchemaPublish?.compositionResult?.subgraphConfigs;

  const entities =
    variantData?.entities?.__typename === 'EntitiesResponse'
      ? variantData.entities.entities
      : undefined;

  const subgraphMetaHelpers = useMemo(
    () => buildSubgraphMetaHelpers(entities, subgraphConfigs, activeSchema),
    [entities, subgraphConfigs, activeSchema],
  );

  const openEditUrlModal = useCallback(
    (openingIntent: 'Set url' | 'Run query') => {
      history.replace({
        ...setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.connectionSettings,
        }),
        state: {
          ...location.state,
          linkEndpointModalData: {
            openingIntent,
          },
        },
      });
    },
    [history, location],
  );

  const localStorageGraphIdentifierForTabState = useLocalStorageGraphIdentifier(
    {
      shouldNamespaceSandboxByEndpoint: false,
      shouldOnlyStoreForRegisteredGraphs: false,
    },
  );

  if (errorLoadingIdentity) {
    // shouldn't hit this because Studio is behind a `<LoggedInRoute/>`
    throw errorLoadingIdentity;
  } else if (loadingIdentity) {
    return <Loading />;
  }

  if (graphRef && initialLoadingSchema && !activeSchema) {
    return <Loading />;
  }

  // TODO: move this to App Layout along with the one in DocumentationPage
  // If someone gets directly to the explorer via the embedded explorer
  // with a graph ref that they don't have access to, show them a PermissionBlock
  if (
    graphRef &&
    !studioPageQueryData?.service?.variant?.permissions?.canQuerySchemas
  ) {
    return (
      <>
        <div className="flex flex-none border-b border-primary bg-primary">
          <div className="mx-8 mb-3 mt-4 text-3xl font-semibold text-primary">
            Explorer
          </div>
        </div>
        <div className="m-8 flex justify-center bg-secondary text-secondary">
          <div className="grow rounded-lg bg-white px-9 py-8 shadow">
            <PermissionBlock />
          </div>
        </div>
      </>
    );
  }

  if (graphRef && (!activeSchema || !connectionSettings)) {
    return (
      <ErrorBoundary>
        <div className="flex h-full flex-col bg-primary text-primary">
          <PageLayout title={<PageTitle title="Explorer" />}>
            <NoSchemaFoundCard
              graphRef={graphRef}
              schemaError={schemaError}
              isContract={variantData?.isContract}
            />
          </PageLayout>
        </div>
      </ErrorBoundary>
    );
  }
  // If we have a graph ref, activeSchema and connectionSettings must be non
  // null from the above check. Both of these should be safe.
  const schema = activeSchema ?? SANDBOX_EMPTY_SCHEMA;
  const safeConnectionSettings =
    connectionSettings ?? sandboxConnectionSettingsWithUrl;

  return (
    <AnalyticsProvider defaultCategory="Studio" userId={userId}>
      <EditorTabStateProviders
        schema={schema}
        explorerBehavioralFlags={explorerBehavioralFlags}
        graphIdentifier={localStorageGraphIdentifierForTabState}
      >
        <ExplorerPageBody
          key={
            graphRef
              ? `${graphRef.graphId}@${graphRef.graphVariant}`
              : 'Sandbox'
          }
          graphRef={graphRef}
          isPublic={!!variantData?.isPublic}
          isCloudSupergraph={
            variantData?.graph.graphType ===
            GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
          }
          connectionSettings={safeConnectionSettings}
          explorerBehavioralFlags={explorerBehavioralFlags}
          schemaInfo={{
            schema,
            hash,
            createdAt,
            hasSupergraphSchema:
              !!studioPageQueryData?.service?.variant?.hasSupergraphSchema,
            isSupergraph: false,
            subgraphMetaHelpers,
          }}
          introspectionFailure={introspectionFailure}
          explorerSettings={explorerSettings}
          openEditUrlModal={openEditUrlModal}
          accountId={studioPageQueryData?.service?.account?.id}
          graphBehavioralFlags={graphBehavioralFlags}
        />
      </EditorTabStateProviders>
    </AnalyticsProvider>
  );
};
